import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'
import { FlaskConical } from 'lucide-react'
import { BrandLogo } from '@/components/generals/brand-logo'
import { clinic } from '@/constants/clinic'

export type AuthFeature = {
    icon: LucideIcon
    text: string
}

interface AuthLayoutProps {
    /** Headline of the dark side panel (desktop only) */
    title: string
    description: string
    features: AuthFeature[]
    children: ReactNode
}

/** Split layout of the sign in and registration pages: brand panel on the left, form card on the right */
const AuthLayout: FC<AuthLayoutProps> = ({
    title,
    description,
    features,
    children
}): ReactNode => {
    return (
        <div className='min-h-screen bg-background lg:grid lg:grid-cols-[minmax(0,39fr)_minmax(0,61fr)]'>
            <a
                href='#konten'
                className='sr-only z-50 rounded-full bg-white px-4 py-2 font-semibold text-ink shadow-lg focus:not-sr-only focus:fixed focus:top-3 focus:left-3'
            >
                Lewati ke formulir
            </a>

            <aside className='relative isolate hidden overflow-hidden bg-ink text-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:justify-between lg:px-12 lg:py-12 xl:px-14'>
                <div className='absolute -top-32 -right-32 -z-10 size-96 rounded-full bg-brand-500/25 blur-3xl' aria-hidden='true' />
                <div className='absolute -bottom-40 -left-24 -z-10 size-96 rounded-full bg-brand-700/30 blur-3xl' aria-hidden='true' />

                <Link
                    href='/'
                    className='self-start rounded-full outline-none focus-visible:ring-4 focus-visible:ring-white/30'
                    aria-label={`${clinic.name}, ke beranda`}
                >
                    <BrandLogo tone='light' />
                </Link>

                <div className='max-w-md py-10'>
                    <p className='text-4xl leading-[1.15] font-extrabold tracking-tight text-balance xl:text-[2.6rem]'>
                        {title}
                    </p>

                    <p className='mt-5 text-base leading-relaxed text-white/75'>
                        {description}
                    </p>

                    <ul className='mt-8 grid gap-4'>
                        {features.map(feature => {
                            const Icon = feature.icon

                            return (
                                <li key={feature.text} className='flex items-center gap-3.5 text-[15px] text-white/85'>
                                    <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-white/10 text-brand-200 ring-1 ring-white/10'>
                                        <Icon className='size-4' aria-hidden='true' />
                                    </span>
                                    {feature.text}
                                </li>
                            )
                        })}
                    </ul>
                </div>

                <p className='text-sm text-white/60'>
                    Proyek akademik. Seluruh data pada aplikasi ini adalah data demo.
                </p>
            </aside>

            <div className='flex min-h-screen flex-col'>
                <div className='flex items-center justify-between gap-4 px-4 pt-4 sm:px-8 sm:pt-6 lg:justify-end'>
                    <Link
                        href='/'
                        className='rounded-full outline-none focus-visible:ring-4 focus-visible:ring-ring/30 lg:hidden'
                        aria-label={`${clinic.name}, ke beranda`}
                    >
                        <BrandLogo />
                    </Link>

                    <span className='inline-flex shrink-0 items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold tracking-wider text-slate-600 uppercase ring-1 ring-slate-200 ring-inset'>
                        <FlaskConical className='size-3.5' aria-hidden='true' />
                        Data demo
                    </span>
                </div>

                <main id='konten' className='flex flex-1 items-start justify-center px-4 py-6 sm:px-8 sm:py-10 lg:items-center'>
                    <div className='w-full max-w-[35rem] rounded-3xl bg-white p-6 shadow-xl shadow-ink/5 ring-1 ring-border sm:p-8'>
                        {children}
                    </div>
                </main>

                <p className='px-4 pb-6 text-center text-sm text-muted-foreground'>
                    © 2026
                    {' '}
                    {clinic.name}
                </p>
            </div>
        </div>
    )
}

export default AuthLayout
